/**************************************************************************************************/
/*   File: /src/app/stores/reducers/editing/reducer.ts                                            */
/*   Project: newplan-frontend                                                                    */
/*   Created Date: Monday April 29th 2019                                                         */
/*   Author: Andrea Zamboni andrea.zamboni@trilogis.it                                            */
/*   -----                                                                                        */
/*   Last Modified: Thu May 30 2019                                                               */
/*   Modified By: Andrea Zamboni                                                                  */
/*   -----                                                                                        */
/*   Copyright (c) 2019 Trilogis s.r.l. for SIAG                                                  */
/**************************************************************************************************/
import * as _ from 'lodash';

import * as fromShared from '../../../shared';
import * as fromActions from '../../actions';
import * as fromStates from '../../states';

/**
 * the dossier editing state reducer
 * @param state The state
 * @param action The action
 */
export function editingReducer(
    state: fromStates.fromEditing.EditingState = fromStates.fromEditing.INITIAL_DOSSIER_EDITING_STATE,
    action: fromActions.fromEditing.DossierEditingActions |
        fromActions.fromMessages.MessagesActions |
        any): fromStates.fromEditing.EditingState {

    switch (action.type) {
        // Reducer for check dossier status
        case fromActions.fromEditing.DossierEditingActionsType.CHECK_DOSSIER_STATUS:
            return {
                ...state,
                isLoading: true
            };
        // Reducer for check dossier status success
        case fromActions.fromEditing.DossierEditingActionsType.CHECK_DOSSIER_STATUS_SUCCESS:
            return {
                ...state,
                dossierStatus: action.payload.status,
                isLoading: false
            };
        // Reducer for check dossier status fail
        case fromActions.fromEditing.DossierEditingActionsType.CHECK_DOSSIER_STATUS_FAIL:
            return {
                ...state,
                messages: [
                    action.payload.message
                ],
                isLoading: false
            };
        // Reducer for load dossier
        case fromActions.fromEditing.DossierEditingActionsType.LOAD_DOSSIER:
            return {
                ...state,
                isLoading: true,
                hasSpatialChange: false,
                hasOtherChange: false,
                hasRegulationChange: false,
                regulationAreaChanged: undefined
            };
        // Reducer for check dossier status success
        case fromActions.fromEditing.DossierEditingActionsType.LOAD_DOSSIER_SUCCESS:
            // get the spatial files data
            const spatialDataFiles = [];
            if (action.payload.dossier &&
                action.payload.dossier.spatialData) {
                const spatialData: fromShared.models.SpatialData = action.payload.dossier.spatialData;
                // const fileModel: fromShared.models.FileModel = new fromShared.models.FileModel();
                const file = new File([], spatialData.userContent.name);
                const fileModel = new fromShared.models.FileModel(spatialData.userContent.id.id, file, false, true);
                spatialDataFiles.push(fileModel);
            }
            // get the spatial files data
            const otherDataFiles = [];
            if (action.payload.dossier &&
                action.payload.dossier.other) {
                const otherData: fromShared.models.LocalDocument = action.payload.dossier.other;
                // const fileModel: fromShared.models.FileModel = new fromShared.models.FileModel();
                const file = new File(
                    [],
                    otherData.name
                );
                const fileModel = new fromShared.models.FileModel(otherData.id.id, file, false, true);
                otherDataFiles.push(fileModel);
            }
            // get the regulations map
            const regulationsFiles = {};

            if (action.payload.dossier &&
                action.payload.dossier.summary &&
                action.payload.dossier.summary.territorialAreas &&
                action.payload.dossier.summary.territorialAreas.length > 0) {

                const ambiti = action.payload.dossier.summary.territorialAreas;
                ambiti.forEach((ambito) => {
                    regulationsFiles[ambito.code] = {
                        originalDoc: {
                            files: [],
                            uploadFinished: false
                        },
                        deltaDoc: {
                            files: [],
                            uploadFinished: false
                        },
                        finalDoc: {
                            files: [],
                            uploadFinished: false
                        }
                    };
                });
            }

            if (action.payload.dossier &&
                action.payload.dossier.territorialAreasRegulations) {
                const territorialAreasRegulations: fromShared.models.LocalDocument = action.payload.dossier.territorialAreasRegulations;
                // const fileModel: fromShared.models.FileModel = new fromShared.models.FileModel();
                const tarKeys = Object.keys(territorialAreasRegulations);

                tarKeys.forEach((areaCode: string) => {
                    const tarObj: fromShared.models.TerritorialAreaRegulations = territorialAreasRegulations[areaCode];
                    const originalFile = new File(
                        [],
                        tarObj.originalDoc.name
                    );
                    const originalFileModel = new fromShared.models.FileModel(tarObj.originalDoc.id.id, originalFile, false, true);
                    const deltaFile = new File(
                        [],
                        tarObj.deltaDoc.name
                    );
                    const deltaFileModel = new fromShared.models.FileModel(tarObj.deltaDoc.id.id, deltaFile, false, true);
                    const finalFile = new File(
                        [],
                        tarObj.finalDoc.name
                    );
                    const finalFileModel = new fromShared.models.FileModel(tarObj.finalDoc.id.id, finalFile, false, true);
                    regulationsFiles[areaCode] = {
                        originalDoc: {
                            files: [originalFileModel],
                            uploadFinished: true
                        },
                        deltaDoc: {
                            files: [deltaFileModel],
                            uploadFinished: true
                        },
                        finalDoc: {
                            files: [finalFileModel],
                            uploadFinished: true
                        }
                    };
                });
            }
            return {
                ...state,
                dossier: {
                    ...action.payload.dossier
                },
                spatialFiles: spatialDataFiles,
                spatialUploadFinished: spatialDataFiles && spatialDataFiles.length > 0 ? true : false,
                otherFiles: otherDataFiles,
                otherUploadFinished: otherDataFiles && otherDataFiles.length > 0 ? true : false,
                regulationsFiles: regulationsFiles,
                isLoading: false
            };
        // Reducer for check dossier status fail
        case fromActions.fromEditing.DossierEditingActionsType.LOAD_DOSSIER_FAIL:
            return {
                ...state,
                messages: [
                    action.payload.message
                ],
                spatialFiles: [],
                spatialUploadFinished: false,
                isLoading: false
            };
        // Reducer for validate dossier
        case fromActions.fromEditing.DossierEditingActionsType.VALIDATE_DOSSIER:
            return {
                ...state,
                isLoading: true,
                isValidating: true,
                isValidated: false
            };
        // Reducer for validate dossier success
        case fromActions.fromEditing.DossierEditingActionsType.VALIDATE_DOSSIER_SUCCESS:
            return {
                ...state,
                isLoading: false,
                isValidated: true,
                isValidating: false
            };
        // Reducer for validate dossier fail
        case fromActions.fromEditing.DossierEditingActionsType.VALIDATE_DOSSIER_FAIL:
            return {
                ...state,
                messages: [
                    action.payload.message
                ],
                isLoading: false,
                isValidated: false,
                isValidating: false
            };
        // Reducer for send dossier
        case fromActions.fromEditing.DossierEditingActionsType.SEND_DOSSIER:
            return {
                ...state,
                isLoading: true
            };
        // Reducer for send dossier success
        case fromActions.fromEditing.DossierEditingActionsType.SEND_DOSSIER_SUCCESS:
            return {
                ...state,
                isLoading: false
            };
        // Reducer for send dossier fail
        case fromActions.fromEditing.DossierEditingActionsType.SEND_DOSSIER_FAIL:
            return {
                ...state,
                messages: [
                    action.payload.message
                ],
                isLoading: false
            };
        // Reducer for notify message
        case fromActions.fromMessages.MessagesActionsType.NOTIFY_MESSAGE:
            return action.payload.message.stateName === fromStates.fromEditing.STATE_NAME ?
                {
                    ...state,
                    messages: [
                        ...state.messages,
                        action.payload.message
                    ]
                } : { ...state };
        // Reducer for message removed
        case fromActions.fromMessages.MessagesActionsType.REMOVE_MESSAGE_ACTION:
            return {
                ...state,
                messages: [...state.messages.filter(x => x !== action.payload.message)]
            };
        // Reducer for action file added: It copy object getting file id and saving it into state.
        case fromActions.fromEditing.SpatialFileActionTypes.SPATIAL_FILE_ADDED:
            return {
                ...state,
                spatialFiles: [
                    ...state.spatialFiles,
                    ...action.payload.files.map(
                        (file: fromShared.models.FileModel) => {
                            return {
                                id: file.id,
                                isUploading: file.isUploading,
                                isUploaded: file.isUploaded,
                                buffer: _.merge({}, file.buffer),
                                rawData: new Blob([file.buffer], { type: file.buffer.type })
                            };
                        }
                    )
                ]
            };
        // Reducer for action file removed fom collection in state
        case fromActions.fromEditing.SpatialFileActionTypes.SPATIAL_FILE_REMOVED:
            return {
                ...state,
                isLoading: true
            };
        // Reducer for action file removed success fom collection in state
        case fromActions.fromEditing.SpatialFileActionTypes.SPATIAL_FILE_REMOVED_SUCCESS:
            const newSpatialFiles = [
                ...state.spatialFiles.filter(file => {
                    return file.id !== action.payload.id;
                })
            ];
            return {
                ...state,
                spatialFiles: [
                    ...newSpatialFiles
                ],
                spatialUploadFinished: newSpatialFiles.length > 0 ?
                    newSpatialFiles.map(
                        file => file.isUploaded
                    ).reduce(
                        /* istanbul ignore next */
                        (accumulator, currentValue) => accumulator && currentValue
                    )
                    : false,
                isLoading: false
            };
        // Refucer for action file removed fail
        case fromActions.fromEditing.SpatialFileActionTypes.SPATIAL_FILE_REMOVED_FAIL:
            return {
                ...state,
                spatialUploadFinished: false,
                messages: [
                    action.payload.message
                ],
                isLoading: false
            };
        // Reducer for file clear action
        case fromActions.fromEditing.SpatialFileActionTypes.SPATIAL_FILE_CLEAR:
            return {
                ...state,
                spatialFiles: []
            };
        // Reducer for file upload action
        case fromActions.fromEditing.SpatialFileActionTypes.SPATIAL_FILE_UPLOAD:
            return {
                ...state,
                spatialFiles: [
                    ...state.spatialFiles.map(
                        file => {
                            if (file.id === action.payload.file.id) {
                                file.isUploading = true;
                            }
                            return file;
                        }
                    )
                ],
                spatialUploadFinished: state.spatialFiles.length > 0 ?
                    state.spatialFiles.map(
                        file => file.isUploaded
                    ).reduce(
                        /* istanbul ignore next */
                        (accumulator, currentValue) => accumulator && currentValue
                    )
                    : false
            };
        // Reducer for file upload success action
        case fromActions.fromEditing.SpatialFileActionTypes.SPATIAL_FILE_UPLOAD_SUCCESS:
            return {
                ...state,
                hasSpatialChange: true,
                spatialFiles: [
                    ...state.spatialFiles.map(
                        file => {
                            if (file.id === action.payload.file.id) {
                                file.id = action.payload.documentId.id;
                                file.isUploading = false;
                                file.isUploaded = true;
                            }
                            return file;
                        }
                    )
                ],
                spatialUploadFinished: state.spatialFiles.length > 0 ?
                    state.spatialFiles.map(
                        file => file.isUploaded
                    ).reduce(
                        /* istanbul ignore next */
                        (accumulator, currentValue) => accumulator && currentValue
                    )
                    : false
            };
        // Reducer for file upload fail action
        case fromActions.fromEditing.SpatialFileActionTypes.SPATIAL_FILE_UPLOAD_FAILURE:
            return {
                ...state,
                spatialFiles: [
                    ...state.spatialFiles.map(
                        file => {
                            if (file.id === action.payload.file.id) {
                                file.isUploading = false;
                                file.isUploaded = false;
                            }
                            return file;
                        }
                    )
                ],
                hasSpatialChange: false,
                spatialUploadFinished: false,
                messages: [
                    action.payload.message
                ]
            };
        // Reducer for action file added: It copy object getting file id and saving it into state.
        case fromActions.fromEditing.OtherFileActionTypes.OTHER_FILE_ADDED:
            return {
                ...state,
                otherFiles: [
                    ...state.otherFiles,
                    ...action.payload.files.map(
                        (file: fromShared.models.FileModel) => {
                            return {
                                id: file.id,
                                isUploading: file.isUploading,
                                isUploaded: file.isUploaded,
                                buffer: _.merge({}, file.buffer),
                                rawData: new Blob([file.buffer], { type: file.buffer.type })
                            };
                        }
                    )
                ]
            };
        // Reducer for action file removed fom collection in state
        case fromActions.fromEditing.OtherFileActionTypes.OTHER_FILE_REMOVED:
            return {
                ...state,
                isLoading: true
            };
        // Reducer for action file removed success fom collection in state
        case fromActions.fromEditing.OtherFileActionTypes.OTHER_FILE_REMOVED_SUCCESS:
            const newOtherFiles = [
                ...state.otherFiles.filter(file => {
                    return file.id !== action.payload.id;
                })
            ];
            return {
                ...state,
                otherFiles: [
                    ...newOtherFiles
                ],
                otherUploadFinished: newOtherFiles.length > 0 ?
                    newOtherFiles.map(
                        file => file.isUploaded
                    ).reduce(
                        /* istanbul ignore next */
                        (accumulator, currentValue) => accumulator && currentValue
                    )
                    : false,
                isLoading: false
            };
        // Refucer for action file removed fail
        case fromActions.fromEditing.OtherFileActionTypes.OTHER_FILE_REMOVED_FAIL:
            return {
                ...state,
                hasOtherChange: false,
                messages: [
                    action.payload.message
                ],
                isLoading: false
            };
        // Reducer for file clear action
        case fromActions.fromEditing.OtherFileActionTypes.OTHER_FILE_CLEAR:
            return {
                ...state,
                otherFiles: []
            };
        // Reducer for file upload action
        case fromActions.fromEditing.OtherFileActionTypes.OTHER_FILE_UPLOAD:
            return {
                ...state,
                otherFiles: [
                    ...state.otherFiles.map(
                        file => {
                            if (file.id === action.payload.file.id) {
                                file.isUploading = true;
                            }
                            return file;
                        }
                    )
                ],
                otherUploadFinished: state.otherFiles.length > 0 ?
                    state.otherFiles.map(
                        file => file.isUploaded
                    ).reduce(
                        (accumulator, currentValue) => accumulator && currentValue
                    )
                    : false
            };
        // Reducer for file upload success action
        case fromActions.fromEditing.OtherFileActionTypes.OTHER_FILE_UPLOAD_SUCCESS:
            return {
                ...state,
                hasOtherChange: true,
                otherFiles: [
                    ...state.otherFiles.map(
                        file => {
                            if (file.id === action.payload.file.id) {
                                file.id = action.payload.documentId.id;
                                file.isUploading = false;
                                file.isUploaded = true;
                            }
                            return file;
                        }
                    )
                ],
                otherUploadFinished: state.otherFiles.length > 0 ?
                    state.otherFiles.map(
                        file => file.isUploaded
                    ).reduce(
                        /* istanbul ignore next */
                        (accumulator, currentValue) => accumulator && currentValue
                    )
                    : false
            };
        // Reducer for file upload fail action
        case fromActions.fromEditing.OtherFileActionTypes.OTHER_FILE_UPLOAD_FAILURE:
            return {
                ...state,
                hasOtherChange: false,
                otherUploadFinished: false,
                otherFiles: [
                    ...state.otherFiles.map(
                        file => {
                            if (file.id === action.payload.file.id) {
                                file.isUploading = false;
                                file.isUploaded = false;
                            }
                            return file;
                        }
                    )
                ],
                messages: [
                    action.payload.message
                ]
            };
        // Reducer for action file added: It copy object getting file id and saving it into state.
        case fromActions.fromEditing.RegulationFileActionTypes.REGULATION_FILE_ADDED:

            const regulationsFilesCopy = {
                ...state.regulationsFiles
            };

            if (action.payload.type === 'originalDoc') {

                regulationsFilesCopy[action.payload.areaCode].originalDoc.uploadFinished = false;
                regulationsFilesCopy[action.payload.areaCode].originalDoc.files = [
                    ...regulationsFilesCopy[action.payload.areaCode].originalDoc.files,
                    ...action.payload.files.map(
                        (file: fromShared.models.FileModel) => {
                            return {
                                id: file.id,
                                isUploading: file.isUploading,
                                isUploaded: file.isUploaded,
                                buffer: _.merge({}, file.buffer),
                                rawData: new Blob([file.buffer], { type: file.buffer.type })
                            };
                        }
                    )
                ];
            }
            if (action.payload.type === 'deltaDoc') {
                regulationsFilesCopy[action.payload.areaCode].deltaDoc.uploadFinished = false;
                regulationsFilesCopy[action.payload.areaCode].deltaDoc.files = [
                    ...regulationsFilesCopy[action.payload.areaCode].deltaDoc.files,
                    ...action.payload.files.map(
                        (file: fromShared.models.FileModel) => {
                            return {
                                id: file.id,
                                isUploading: file.isUploading,
                                isUploaded: file.isUploaded,
                                buffer: _.merge({}, file.buffer),
                                rawData: new Blob([file.buffer], { type: file.buffer.type })
                            };
                        }
                    )
                ];
            }
            if (action.payload.type === 'finalDoc') {
                regulationsFilesCopy[action.payload.areaCode].finalDoc.uploadFinished = false;
                regulationsFilesCopy[action.payload.areaCode].finalDoc.files = [
                    ...regulationsFilesCopy[action.payload.areaCode].finalDoc.files,
                    ...action.payload.files.map(
                        (file: fromShared.models.FileModel) => {
                            return {
                                id: file.id,
                                isUploading: file.isUploading,
                                isUploaded: file.isUploaded,
                                buffer: _.merge({}, file.buffer),
                                rawData: new Blob([file.buffer], { type: file.buffer.type })
                            };
                        }
                    )
                ];
            }
            return {
                ...state,
                regulationsFiles: {
                    ...regulationsFilesCopy
                }
            };
        // Reducer for action file removed fom collection in state
        case fromActions.fromEditing.RegulationFileActionTypes.REGULATION_FILE_REMOVED:
            return {
                ...state,
                isLoading: true
            };
        // Reducer for action file removed success fom collection in state
        case fromActions.fromEditing.RegulationFileActionTypes.REGULATION_FILE_REMOVED_SUCCESS:

            const regulationsFilesCopyFileRemovedSuccess = {
                ...state.regulationsFiles
            };

            if (action.payload.type === 'originalDoc') {

                regulationsFilesCopyFileRemovedSuccess[action.payload.areaCode].originalDoc.files = [
                    ...regulationsFilesCopyFileRemovedSuccess[action.payload.areaCode].originalDoc.files.filter(file => {
                        return file.id !== action.payload.id;
                    })
                ];
                regulationsFilesCopyFileRemovedSuccess[action.payload.areaCode].originalDoc.uploadFinished =
                    regulationsFilesCopyFileRemovedSuccess[action.payload.areaCode].originalDoc.files.length > 0 ?
                        regulationsFilesCopyFileRemovedSuccess[action.payload.areaCode].originalDoc.files.map(
                            file => file.isUploaded
                        ).reduce(
                            /* istanbul ignore next */
                            (accumulator, currentValue) => accumulator && currentValue
                        )
                        : false;
            }
            if (action.payload.type === 'deltaDoc') {
                regulationsFilesCopyFileRemovedSuccess[action.payload.areaCode].deltaDoc.files = [
                    ...regulationsFilesCopyFileRemovedSuccess[action.payload.areaCode].deltaDoc.files.filter(file => {
                        return file.id !== action.payload.id;
                    })
                ];
                regulationsFilesCopyFileRemovedSuccess[action.payload.areaCode].deltaDoc.uploadFinished =
                    regulationsFilesCopyFileRemovedSuccess[action.payload.areaCode].deltaDoc.files.length > 0 ?
                        regulationsFilesCopyFileRemovedSuccess[action.payload.areaCode].deltaDoc.files.map(
                            file => file.isUploaded
                        ).reduce(
                            /* istanbul ignore next */
                            (accumulator, currentValue) => accumulator && currentValue
                        )
                        : false;
            }
            if (action.payload.type === 'finalDoc') {
                regulationsFilesCopyFileRemovedSuccess[action.payload.areaCode].finalDoc.files = [
                    ...regulationsFilesCopyFileRemovedSuccess[action.payload.areaCode].finalDoc.files.filter(file => {
                        return file.id !== action.payload.id;
                    })
                ];
                regulationsFilesCopyFileRemovedSuccess[action.payload.areaCode].finalDoc.uploadFinished =
                    regulationsFilesCopyFileRemovedSuccess[action.payload.areaCode].finalDoc.files.length > 0 ?
                        regulationsFilesCopyFileRemovedSuccess[action.payload.areaCode].finalDoc.files.map(
                            file => file.isUploaded
                        ).reduce(
                            /* istanbul ignore next */
                            (accumulator, currentValue) => accumulator && currentValue
                        )
                        : false;
            }
            return {
                ...state,
                isLoading: false,
                regulationsFiles: {
                    ...regulationsFilesCopyFileRemovedSuccess
                }
            };
        // Refucer for action file removed fail
        case fromActions.fromEditing.RegulationFileActionTypes.REGULATION_FILE_REMOVED_FAIL:
            return {
                ...state,
                isLoading: false,
                messages: [
                    action.payload.message
                ]
            };
        // Reducer for file clear action
        case fromActions.fromEditing.RegulationFileActionTypes.REGULATION_FILE_CLEAR:
            const regulationsFilesCopyFileClear = {
                ...state.regulationsFiles
            };

            if (action.payload.type === 'originalDoc') {
                regulationsFilesCopyFileClear[action.payload.areaCode].originalDoc.uploadFinished = false;
                regulationsFilesCopyFileClear[action.payload.areaCode].originalDoc.files = [];
            }
            if (action.payload.type === 'deltaDoc') {
                regulationsFilesCopyFileClear[action.payload.areaCode].deltaDoc.uploadFinished = false;
                regulationsFilesCopyFileClear[action.payload.areaCode].deltaDoc.files = [];
            }
            if (action.payload.type === 'finalDoc') {
                regulationsFilesCopyFileClear[action.payload.areaCode].finalDoc.uploadFinished = false;
                regulationsFilesCopyFileClear[action.payload.areaCode].finalDoc.files = [];
            }
            return {
                ...state,
                regulationsFiles: {
                    ...regulationsFilesCopyFileClear
                }
            };
        // Reducer for file upload action
        case fromActions.fromEditing.RegulationFileActionTypes.REGULATION_FILE_UPLOAD:

            const regulationsFilesCopyFileUpload = {
                ...state.regulationsFiles
            };

            if (action.payload.type === 'originalDoc') {

                regulationsFilesCopyFileUpload[action.payload.areaCode].originalDoc.files = [
                    ...regulationsFilesCopyFileUpload[action.payload.areaCode].originalDoc.files.map(
                        file => {
                            if (file.id === action.payload.file.id) {
                                file.isUploading = true;
                            }
                            return file;
                        }
                    )
                ];
            }
            if (action.payload.type === 'deltaDoc') {
                regulationsFilesCopyFileUpload[action.payload.areaCode].deltaDoc.files = [
                    ...regulationsFilesCopyFileUpload[action.payload.areaCode].deltaDoc.files.map(
                        file => {
                            if (file.id === action.payload.file.id) {
                                file.isUploading = true;
                            }
                            return file;
                        }
                    )
                ];
            }
            if (action.payload.type === 'finalDoc') {
                regulationsFilesCopyFileUpload[action.payload.areaCode].finalDoc.files = [
                    ...regulationsFilesCopyFileUpload[action.payload.areaCode].finalDoc.files.map(
                        file => {
                            if (file.id === action.payload.file.id) {
                                file.isUploading = true;
                            }
                            return file;
                        }
                    )
                ];
            }
            return {
                ...state,
                hasRegulationChange: false,
                regulationAreaChanged: undefined,
                regulationsFiles: {
                    ...regulationsFilesCopyFileUpload
                }
            };
        // Reducer for file upload success action
        case fromActions.fromEditing.RegulationFileActionTypes.REGULATION_FILE_UPLOAD_SUCCESS:

            const regulationsFilesCopyFileUploadSuccess = {
                ...state.regulationsFiles
            };

            if (action.payload.type === 'originalDoc') {

                regulationsFilesCopyFileUploadSuccess[action.payload.areaCode].originalDoc.files = [
                    ...regulationsFilesCopyFileUploadSuccess[action.payload.areaCode].originalDoc.files.map(
                        file => {
                            if (file.id === action.payload.file.id) {
                                file.id = action.payload.documentId.id;
                                file.isUploading = false;
                                file.isUploaded = true;
                            }
                            return file;
                        }
                    )
                ];
                regulationsFilesCopyFileUploadSuccess[action.payload.areaCode].originalDoc.uploadFinished = true;
            }
            if (action.payload.type === 'deltaDoc') {
                regulationsFilesCopyFileUploadSuccess[action.payload.areaCode].deltaDoc.files = [
                    ...regulationsFilesCopyFileUploadSuccess[action.payload.areaCode].deltaDoc.files.map(
                        file => {
                            if (file.id === action.payload.file.id) {
                                file.id = action.payload.documentId.id;
                                file.isUploading = false;
                                file.isUploaded = true;
                            }
                            return file;
                        }
                    )
                ];
                regulationsFilesCopyFileUploadSuccess[action.payload.areaCode].deltaDoc.uploadFinished = true;
            }
            if (action.payload.type === 'finalDoc') {
                regulationsFilesCopyFileUploadSuccess[action.payload.areaCode].finalDoc.files = [
                    ...regulationsFilesCopyFileUploadSuccess[action.payload.areaCode].finalDoc.files.map(
                        file => {
                            if (file.id === action.payload.file.id) {
                                file.id = action.payload.documentId.id;
                                file.isUploading = false;
                                file.isUploaded = true;
                            }
                            return file;
                        }
                    )
                ];
                regulationsFilesCopyFileUploadSuccess[action.payload.areaCode].finalDoc.uploadFinished = true;
            }
            return {
                ...state,
                hasRegulationChange: true,
                regulationAreaChanged: action.payload.areaCode,
                regulationsFiles: {
                    ...regulationsFilesCopyFileUploadSuccess
                }
            };
        // Reducer for file upload fail action
        case fromActions.fromEditing.RegulationFileActionTypes.REGULATION_FILE_UPLOAD_FAILURE:
            const regulationsFilesCopyFileUploadFail = {
                ...state.regulationsFiles
            };

            if (action.payload.type === 'originalDoc') {

                regulationsFilesCopyFileUploadFail[action.payload.areaCode].originalDoc.files = [
                    ...regulationsFilesCopyFileUploadFail[action.payload.areaCode].originalDoc.files.map(
                        file => {
                            if (file.id === action.payload.file.id) {
                                file.isUploading = false;
                                file.isUploaded = false;
                            }
                            return file;
                        }
                    )
                ];
                regulationsFilesCopyFileUploadFail[action.payload.areaCode].originalDoc.uploadFinished = true;
            }
            if (action.payload.type === 'deltaDoc') {
                regulationsFilesCopyFileUploadFail[action.payload.areaCode].deltaDoc.files = [
                    ...regulationsFilesCopyFileUploadFail[action.payload.areaCode].deltaDoc.files.map(
                        file => {
                            if (file.id === action.payload.file.id) {
                                file.isUploading = false;
                                file.isUploaded = false;
                            }
                            return file;
                        }
                    )
                ];
                regulationsFilesCopyFileUploadFail[action.payload.areaCode].deltaDoc.uploadFinished = true;
            }
            if (action.payload.type === 'finalDoc') {
                regulationsFilesCopyFileUploadFail[action.payload.areaCode].finalDoc.files = [
                    ...regulationsFilesCopyFileUploadFail[action.payload.areaCode].finalDoc.files.map(
                        file => {
                            if (file.id === action.payload.file.id) {
                                file.isUploading = false;
                                file.isUploaded = false;
                            }
                            return file;
                        }
                    )
                ];
                regulationsFilesCopyFileUploadFail[action.payload.areaCode].finalDoc.uploadFinished = true;
            }
            return {
                ...state,
                hasRegulationChange: false,
                regulationAreaChanged: undefined,
                regulationsFiles: {
                    ...regulationsFilesCopyFileUploadFail
                },
                messages: [
                    action.payload.message
                ]
            };
        // XXX

        // reducer for file create or upload for spatial data
        case fromActions.fromEditing.DossierEditingActionsType.UPDATE_SPATIAL_DATA_DOSSIER:
            return {
                ...state,
                isLoading: true
            };
        // reducer for file create or upload for spatial data success
        case fromActions.fromEditing.DossierEditingActionsType.UPDATE_SPATIAL_DATA_DOSSIER_SUCCESS:
            return {
                ...state,
                hasSpatialChange: false,
                isLoading: false
            };
        // reducer for file create or upload for spatial data fail
        case fromActions.fromEditing.DossierEditingActionsType.UPDATE_SPATIAL_DATA_DOSSIER_FAIL:
            return {
                ...state,
                hasSpatialChange: false,
                messages: [
                    action.payload.message
                ],
                isLoading: false
            };
        // reducer for file create or upload for other data
        case fromActions.fromEditing.DossierEditingActionsType.UPDATE_OTHER_DATA_DOSSIER:
            return {
                ...state,
                isLoading: true
            };
        // reducer for file create or upload for other data success
        case fromActions.fromEditing.DossierEditingActionsType.UPDATE_OTHER_DATA_DOSSIER_SUCCESS:
            return {
                ...state,
                hasOtherChange: false,
                isLoading: false
            };
        // reducer for file create or upload for other data fail
        case fromActions.fromEditing.DossierEditingActionsType.UPDATE_OTHER_DATA_DOSSIER_FAIL:
            return {
                ...state,
                hasOtherChange: false,
                messages: [
                    action.payload.message
                ],
                isLoading: false
            };
        // reducer for file create or upload for other data
        case fromActions.fromEditing.DossierEditingActionsType.UPDATE_REGULATION_DATA_DOSSIER:
            return {
                ...state,
                isLoading: true
            };
        // reducer for file create or upload for other data success
        case fromActions.fromEditing.DossierEditingActionsType.UPDATE_REGULATION_DATA_DOSSIER_SUCCESS:
            return {
                ...state,
                hasRegulationChange: false,
                regulationAreaChanged: undefined,
                isLoading: false
            };
        // reducer for file create or upload for other data fail
        case fromActions.fromEditing.DossierEditingActionsType.UPDATE_REGULATION_DATA_DOSSIER_FAIL:
            return {
                ...state,
                hasRegulationChange: false,
                regulationAreaChanged: undefined,
                messages: [
                    action.payload.message
                ],
                isLoading: false
            };
        // Reducer for unset user
        case fromActions.fromUi.UiActionTypes.UNSET_LOGGED_USER:
            return {
                ...fromStates.fromEditing.INITIAL_DOSSIER_EDITING_STATE
            };
        default:
            return state;
    }
}

// Helpers
/**
 * The getIsLoading function helper
 * @param state The state
 */
const getIsLoading = (state: fromStates.fromEditing.EditingState) => state.isLoading;
/**
 * The getIsValidating function helper
 * @param state The state
 */
const getIsValidating = (state: fromStates.fromEditing.EditingState) => state.isValidating;
/**
 * The getIsValidated function helper
 * @param state The state
 */
const getIsValidated = (state: fromStates.fromEditing.EditingState) => state.isValidated;
/**
 * The getIsLoaded function helper
 * @param state The state
 */
const getIsLoaded = (state: fromStates.fromEditing.EditingState) => state.isLoaded;
/**
 * The get messages helper function
 * @param state The state
 */
const getMessages = (state: fromStates.fromEditing.EditingState) => state.messages;
/**
 * Get the dossier details
 * @param state The state
 */
const getDossier = (state: fromStates.fromEditing.EditingState) => state.dossier;
/**
 * Get the dossier id
 * @param state The state
 */
const getDossierId = (state: fromStates.fromEditing.EditingState) => state.dossierId;
/**
 * Get the dossier status
 * @param state The state
 */
const getDossierStatus = (state: fromStates.fromEditing.EditingState) => state.dossierStatus;
/**
 * Get spatial files
 * @param state The state
 */
const getSpatialFiles = (state: fromStates.fromEditing.EditingState) => state.spatialFiles;
/**
 * Get spatial upload finished
 * @param state The state
 */
const getSpatialUploadFinished = (state: fromStates.fromEditing.EditingState) => state.spatialUploadFinished;
/**
 * Get other files
 * @param state The state
 */
const getOtherFiles = (state: fromStates.fromEditing.EditingState) => state.otherFiles;
/**
 * Get other upload finished
 * @param state The state
 */
const getOtherUploadFinished = (state: fromStates.fromEditing.EditingState) => state.otherUploadFinished;
/**
 * Get true if dossier has spatial changes
 * @param state The state
 */
const getDossierHasSpatialChange = (state: fromStates.fromEditing.EditingState) => state.hasSpatialChange;
/**
 * Get true if dossier has other changes
 * @param state The state
 */
const getDossierHasOtherChange = (state: fromStates.fromEditing.EditingState) => state.hasOtherChange;
/**
 * Get the regulations state
 * @param state The state
 */
const getRegulationsFiles = (state: fromStates.fromEditing.EditingState) => state.regulationsFiles;
/**
 * Get the regulations change
 * @param state The state
 */
const getDossierHasRegulationChange = (state: fromStates.fromEditing.EditingState) => state.hasRegulationChange;
/**
 * Get the regulations area change
 * @param state The state
 */
const getRegulationAreaChanged = (state: fromStates.fromEditing.EditingState) => state.regulationAreaChanged;
/**
 * The helpers
 */
export const helpers = {
    getIsLoading,
    getIsValidating,
    getIsValidated,
    getIsLoaded,
    getMessages,
    getDossier,
    getDossierId,
    getDossierStatus,
    getSpatialFiles,
    getSpatialUploadFinished,
    getOtherFiles,
    getOtherUploadFinished,
    getDossierHasSpatialChange,
    getDossierHasOtherChange,
    getRegulationsFiles,
    getDossierHasRegulationChange,
    getRegulationAreaChanged
};


